import type { AutobuildIngress } from '@a7y/types';

/**
 * Which ingress started a session.
 *
 * Renders for every KNOWN value except a bare cron. Null renders NOTHING: AHC
 * and APD record no ingress, and neither did any session predating the marker
 * — claiming a provenance we did not record is the failure the
 * null-is-not-scheduled rule exists to prevent.
 *
 * A7Y-3962 D7 added a fallback here: a row whose component was
 * `ingress-scheduler` got a grey `Ingress scheduler` chip, on the reasoning
 * that those rows are the majority and would otherwise carry no chip at all.
 * It is removed, because the chip was never anything but a second copy. An
 * `ingress-scheduler` row exists only under CDID, CDID is the only kind that
 * renders the Component column, and that column already reads `Ingress
 * scheduler` — so the fallback printed the same two words twice on the same
 * row, roughly 1,224 times a week (measured over the 7 days to 2026-09-15,
 * where scheduler attempts were 52% of all CDID-family runs). The component is
 * still shown; it is simply shown once.
 */
const STYLES: Partial<
  Record<AutobuildIngress, { label: string; className: string; title: string }>
> = {
  'cdid-schedule': {
    label: 'Scheduled',
    className: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300',
    title: 'Started by the 6-hour cron (cdid.yml)',
  },
  'cdid-hot-lane': {
    label: 'Hot lane',
    className: 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300',
    title:
      'Started by the 20-minute urgent hot lane (cdid-hot-lane.yml) for a priority-1 ticket no scheduled session would reach soon',
  },
  adgs: {
    label: 'ADGS',
    className: 'bg-teal-100 text-teal-800 dark:bg-teal-950 dark:text-teal-300',
    title: 'Started by the deploy-gate dispatcher (adgs.yml) once a gated ticket became eligible',
  },
};

export function IngressBadge({ ingress }: { ingress: AutobuildIngress | null }) {
  const style = ingress != null ? STYLES[ingress] : null;
  if (!style) return null;
  return (
    <span
      className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap ${style.className}`}
      title={style.title}
    >
      {style.label}
    </span>
  );
}
